import { Stack, Text, SimpleGrid, Card, Group, Anchor, Title, Code, Skeleton } from '@mantine/core';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ReactNode, KeyboardEvent } from 'react';
import { useCasesList } from '../api/cases';
import { useDuplicatesList } from '../api/duplicates';
import { usePalvelutunnukset } from '../api/palvelutunnukset';
import { useImportsList } from '../api/imports';
import { CardTypeBadge } from '../components/CardTypeBadge';
import { StateBadge } from '../components/StateBadge';
import { PageHeader } from '../components/PageHeader';
import { ErrorState } from '../components/ErrorState';
import { TableSkeleton } from '../components/TableSkeleton';
import { cardType } from '@taksikortti/shared';
import { t } from '../lib/strings';
import { formatDateTime } from '../lib/dates';

function Kpi({ label, value, to, search }: {
  label: string; value: ReactNode; to?: string; search?: Record<string, unknown>;
}) {
  const body = (
    <Card>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text fz={28} fw={700}>{value}</Text>
    </Card>
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return to ? <Anchor component={Link} to={to as any} search={search as any} underline="never">{body}</Anchor> : body;
}

export function DashboardPage() {
  const navigate = useNavigate();
  // Counts come from `total` (pageSize:1) — no need to pull thousands of rows.
  const openCount = useCasesList({ status: ['open', 'query_sent', 'reminder_sent'], pageSize: 1 });
  const reminderCases = useCasesList({ status: ['reminder_sent'], pageSize: 50 });
  const reviewDups = useDuplicatesList({ status: 'review_required', pageSize: 50 });
  const pendingDups = useDuplicatesList({ status: 'pending', pageSize: 1 });
  const palv = usePalvelutunnukset({ minCases: 2 });
  const imports = useImportsList();

  const loading = openCount.isLoading || reminderCases.isLoading || reviewDups.isLoading || pendingDups.isLoading;
  const isError = openCount.isError || reminderCases.isError || reviewDups.isError || pendingDups.isError;

  if (loading) {
    return (
      <Stack gap="md">
        <PageHeader title={t.dashboard.title} subtitle={t.dashboard.subtitle} />
        <SimpleGrid cols={{ base: 2, md: 4 }}>
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={76} radius="md" />)}
        </SimpleGrid>
        <TableSkeleton rows={4} />
      </Stack>
    );
  }
  if (isError) {
    return (
      <Stack gap="md">
        <PageHeader title={t.dashboard.title} subtitle={t.dashboard.subtitle} />
        <ErrorState
          error={openCount.error ?? reviewDups.error ?? pendingDups.error ?? reminderCases.error}
          onRetry={() => { void openCount.refetch(); void reviewDups.refetch(); void pendingDups.refetch(); void reminderCases.refetch(); }}
        />
      </Stack>
    );
  }

  const reminder = reminderCases.data?.rows ?? [];
  // A failed side query must not masquerade as "0" / "no imports" — show a dash / error line.
  const repeatTerminals = palv.isError ? '—' : (palv.data?.rows ?? []).filter((p) => p.totalCases >= 2).length;
  const lastImport = imports.data?.rows?.[0];
  const triageEmpty = (reviewDups.data?.rows.length ?? 0) === 0 && reminder.length === 0;

  return (
    <Stack gap="md">
      <PageHeader title={t.dashboard.title} subtitle={t.dashboard.subtitle} />

      <SimpleGrid cols={{ base: 2, md: 4 }}>
        <Kpi label={t.dashboard.openCases} value={openCount.data?.total ?? 0} to="/cases" search={{ kind: 'case' }} />
        <Kpi label={t.dashboard.reviewRequired} value={reviewDups.data?.total ?? 0} to="/cases" search={{ kind: 'duplicate', state: ['tarkistettava'] }} />
        <Kpi label={t.dashboard.pendingDups} value={pendingDups.data?.total ?? 0} to="/cases" search={{ kind: 'duplicate', state: ['avoin'] }} />
        <Kpi label={t.dashboard.repeatTerminals} value={repeatTerminals} to="/palvelutunnukset" />
      </SimpleGrid>

      <Card>
        <Group justify="space-between">
          <Text fw={500}>{t.dashboard.lastImport}</Text>
          <Anchor component={Link} to="/import" size="sm">{t.dashboard.viewAll}</Anchor>
        </Group>
        {imports.isError ? (
          <Text size="sm" c="red">{t.errors.title}</Text>
        ) : lastImport ? (
          <Text size="sm" c="dimmed">
            {formatDateTime(lastImport.startedAt)} · {lastImport.fileName} · {lastImport.importedCount} uutta · {lastImport.newCasesCount} tapausta
          </Text>
        ) : <Text size="sm" c="dimmed">{t.dashboard.noImports}</Text>}
      </Card>

      <Title order={4}>{t.dashboard.triageTitle}</Title>
      {triageEmpty ? (
        <Text c="dimmed" size="sm">{t.dashboard.triageEmpty}</Text>
      ) : (
        <Stack gap={4}>
          {reviewDups.data?.rows.map((d) => {
            const open = () => void navigate({ to: '/cases/tupla/$korttinumero/$groupId', params: { korttinumero: d.korttinumero, groupId: d.groupId } });
            return (
              <Card
                key={`d-${d.groupId}`}
                padding="xs"
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                onClick={open}
                onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
              >
                <Group gap="sm">
                  <CardTypeBadge type={cardType(d.korttinumero)} />
                  <StateBadge status="review_required" />
                  <Code>{d.korttinumero}</Code>
                  <Text size="sm" c="dimmed">{d.asiakas ?? d.asiakasnumero ?? ''}</Text>
                </Group>
              </Card>
            );
          })}
          {reminder.map((c) => {
            const open = () => void navigate({ to: '/cases/$korttinumero/$caseId', params: { korttinumero: c.korttinumero, caseId: c.caseId } });
            return (
              <Card
                key={`c-${c.caseId}`}
                padding="xs"
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                onClick={open}
                onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
              >
                <Group gap="sm">
                  <CardTypeBadge type={cardType(c.korttinumero)} />
                  <StateBadge status="reminder_sent" />
                  <Code>{c.korttinumero}</Code>
                  <Text size="sm" c="dimmed">{c.yritys ?? ''}</Text>
                </Group>
              </Card>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
